<template>
	<Skeleton :paragraph="{ rows: 14 }" :title="false" :loading="loading">
		<Table
			:columns="columns"
			:data-source="rtcmMessages"
			:row-selection="rowSelection"
			:pagination="false"
			size="small"
		>
			<template #bodyCell="{ column, record }">
				<template v-if="column.dataIndex === 'period'">
					<InputDecimal v-model="record.period" :allowPoint="false" range="non-negative" size="small" :disabled="disabled" />
				</template>
				<template v-else-if="column.dataIndex === 'label'">
					{{ $t(`net-info-table.rtcm-messages.description.${record.label}`, $t('net-info-table.rtcm-messages.description.unknown')) }}
				</template>
			</template>
		</Table>
		<div class="pd-md-vertical">
			<TypographyText class="display-block">{{ selectedCountDisplay }}</TypographyText>
			<TypographyText class="display-block">
				{{ isPeriodChangedDisplay }}
				<div v-if="isSomePeriodChanged">
					<div class="nobr-inline" v-for="displayItem in periodDisplayItems" :key="displayItem.code">
						<TypographyText code>{{ displayItem.code }}</TypographyText> — {{ displayItem.periodText }}
					</div>
				</div>
			</TypographyText>
		</div>
		<Button class="display-block pd-vertical" type="primary" @click="onSubmit" :loading="isSubmitting" :disabled="settings.deleted || disabled">
			{{ $t('settings.vrsSettingsForm.buttonLabel') }}
		</Button>
	</Skeleton>
</template>

<script>
import { toRef, ref, watch, computed, nextTick } from 'vue';
import { TypographyText, Skeleton, Button, Table, message } from 'ant-design-vue';

import { useI18n } from 'vue-i18n';

import Api from '@/api/Api';

import Consts from '@/api/Consts';
import Validators from '@/api/Validators';

import InputDecimal from '@/components/common/InputDecimal';

import { showErrorMessage } from '@/components/common/errorMessage';
import { useProvideInjectFormChangedStatuses } from '@/utils/useProvideInject';


export default {
	components: {
		TypographyText, Skeleton, Table, Button,
		InputDecimal,
	},
	props: {
		loading: {},
		status: {},
		settings: {},
		rtcmFilters: {},
		isVrs: {},
		fetchSettings: {},
		disabled: {
			type: Boolean,
			default: false,
		},
	},
	setup(props) {
		const { t } = useI18n();
		const rtcmMessages = ref([]);
		const selectedRowKeys = ref([]);

		const { injectValue } = useProvideInjectFormChangedStatuses;
		const formChangedStatuses = injectValue();
		const { enableWatch, pauseWatch, resetChangedState } = Validators.useWatchFormChanges(
			[rtcmMessages, selectedRowKeys],
			formChangedStatuses,
			'RtcmFiltersForm',
		);

		const statusDataRef = toRef(props, 'status');
		const rtcmFiltersRef = toRef(props, 'rtcmFilters');
		const isVrsRef = toRef(props, 'isVrs');

		const allRtcmMessagesForMode = computed(() => statusDataRef.value.allRtcmMessages.filter(item => props.isVrs || !item.vrs_only));
	
		const updateRtcmMessages = () => {
			pauseWatch();
			let selectedIds = [];
			rtcmMessages.value = allRtcmMessagesForMode.value.map(item => {
				const initialItem = rtcmFiltersRef.value.rtcm_messages_filters.find(initialItem => initialItem.code === item.code);
				if (initialItem != null && initialItem.checked) {
					selectedIds.push(item.code);
				}
				return {
					...item,
					key: item.code,
					period: (initialItem != null && initialItem.period > 0) ? initialItem.period : '',
				}
			});
			selectedRowKeys.value = selectedIds;
			nextTick(enableWatch);
		};

		watch([statusDataRef, rtcmFiltersRef], () => {
			updateRtcmMessages();
		}, { immediate: true });
		
		const onSelect = keys => {
			selectedRowKeys.value = keys;
		};
		const rowSelection = computed(() => ({
			selectedRowKeys: selectedRowKeys.value,
			onChange: onSelect,
			getCheckboxProps: () => ({
				disabled: props.disabled,
			}),
		}));

		const isSubmitting = ref(false);
		const onSubmit = () => {
			let data = [];
			for (const item of rtcmMessages.value) {
				const isSelected = selectedRowKeys.value.length === 0 || selectedRowKeys.value.includes(item.key);
				if (isSelected || (item.period !== '' && Number(item.period) !== 0)) {
					data.push({
						code: item.key,
						period: item.period || 0,
						checked: selectedRowKeys.value.includes(item.key),
					});
				}
			}
			isSubmitting.value = true;
			Api.rtcmfMountpointUpdate(rtcmFiltersRef.value.id, { rtcm_messages_filters: data }, isVrsRef.value, response => {
				isSubmitting.value = false;
				if (response.status === 'ok') {
					props.fetchSettings(() => {resetChangedState()}, true); // hide loading
					message.success(t('settings.vrsSettingsForm.messages.success'));
				} else {
					showErrorMessage(response.errors);
				}
			});
		}

		const selectedCountDisplay = computed(() => {
			if (selectedRowKeys.value.length === 0 || selectedRowKeys.value.length === allRtcmMessagesForMode.value.length) {
				return t('settings.vrsSettingsForm.rtcmMessagesFilters.filters.allMessages');
			}
			return t('settings.vrsSettingsForm.rtcmMessagesFilters.filters.selectedMessages', selectedRowKeys.value.length);
		});
		const isItemPeriodChanged = (period) => {
			return period !== '' && Number(period) !== 0;
		}
		const getItemPeriodText = (period) => {
			if (isItemPeriodChanged(period)) {
				return t('settings.vrsSettingsForm.rtcmMessagesFilters.period.seconds', Number(period));
			}
			return t('settings.vrsSettingsForm.rtcmMessagesFilters.period.byDefault');
		}
		const selectedItems = computed(() => {
			return rtcmMessages.value.filter(
				item => (selectedRowKeys.value.length === 0 || selectedRowKeys.value.includes(item.key))
			)
		})
		const periodDisplayItems = computed(() => {
			const msgs = selectedItems.value;
			return msgs.map((item, index) => ({
				code: item.code,
				periodText: getItemPeriodText(item.period) + (index < msgs.length - 1 ? ',' : '.'),
			}))
		});
		const isSomePeriodChanged = computed(() => {
			return selectedItems.value.filter(item => isItemPeriodChanged(item.period)).length > 0;
		})
		const isPeriodChangedDisplay = computed(() => (
			t('settings.vrsSettingsForm.rtcmMessagesFilters.period.' + (isSomePeriodChanged.value ? 'someChanged' : 'allDefault'))
		));
		return {
			rtcmMessages,
			selectedRowKeys,
			rowSelection,
			isSubmitting,
			onSubmit,
			columns: Consts.rtcmMessagesFiltersTableColumnsComp,
			selectedCountDisplay,
			isSomePeriodChanged,
			isPeriodChangedDisplay,
			periodDisplayItems,
		};
	},
}
</script>
